/**
 * O painel "Contato" da conversa (spec 056): o nome e o e-mail da pessoa, e o
 * cliente e o ticket a que a conversa está ligada, editados sem sair do chat,
 * como o painel de contato da Blip fazia. O WhatsApp é o endereço da conversa e
 * só aparece.
 *
 * A conversa sem nome (o telefone no lugar) abre o campo de nome vazio. Salvar só
 * o e-mail mantém o telefone como está, e a API grava uma nota interna com o
 * antes e o depois.
 */
import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Ticket } from 'lucide-react';
import {
  CHAT_REQUESTOR_EMAIL_MAX,
  CHAT_REQUESTOR_NAME_MAX,
  chatHasName,
  type ChatDetail,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { EmptyState, Separator, Spinner } from '@/components/ui/misc';
import { Sheet } from '@/components/ui/sheet';
import { chatDetailOptions } from './chats-queries';
import { useUpdateChatContact } from './chats-mutations';
import { errorMessage, formatWaPhone } from './chats-format';
import { LinkClientDialog, LinkTicketDialog } from './chat-dialogs';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactSheet({
  chatId,
  open,
  onClose,
}: {
  chatId: number;
  open: boolean;
  onClose: () => void;
}) {
  const detailQ = useQuery({ ...chatDetailOptions(chatId), enabled: open });
  return (
    <Sheet open={open} onClose={onClose} title="Contato" description="A pessoa desta conversa">
      {detailQ.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      ) : detailQ.isError || !detailQ.data ? (
        <EmptyState message={`Erro: ${errorMessage(detailQ.error)}`} />
      ) : (
        // a chave remonta o formulário quando o painel troca de conversa
        <ContactBody key={detailQ.data.id} chat={detailQ.data} />
      )}
    </Sheet>
  );
}

function ContactBody({ chat }: { chat: ChatDetail }) {
  const save = useUpdateChatContact(chat.id);
  const named = chatHasName(chat);
  const [name, setName] = useState(named ? chat.requestorName : '');
  const [email, setEmail] = useState(chat.requestorEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [dialog, setDialog] = useState<'client' | 'ticket' | null>(null);
  const archived = chat.status === 'archived';

  const typedName = name.replace(/\s+/g, ' ').trim();
  // sem nome digitado, a conversa sem nome segue com o telefone (salvar só o e-mail)
  const nextName = typedName || (named ? '' : chat.requestorName);
  const nextEmail = email.trim() || null;
  const dirty = nextName !== chat.requestorName || nextEmail !== chat.requestorEmail;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (nextName === '') {
      setError('Informe o nome.');
      return;
    }
    if (nextEmail !== null && !EMAIL.test(nextEmail)) {
      setError('E-mail inválido.');
      return;
    }
    save.mutate(
      { requestorName: nextName, requestorEmail: nextEmail },
      {
        onSuccess: (detail) => {
          setSaved(true);
          setName(chatHasName(detail) ? detail.requestorName : '');
          setEmail(detail.requestorEmail ?? '');
        },
        onError: (err) => setError(errorMessage(err)),
      }
    );
  };

  return (
    <div className="space-y-5">
      <form className="space-y-3" onSubmit={submit} aria-label="Dados do contato">
        <div className="space-y-1.5">
          <Label htmlFor="contact-name">Nome</Label>
          <Input
            id="contact-name"
            value={name}
            maxLength={CHAT_REQUESTOR_NAME_MAX}
            placeholder={named ? undefined : 'Sem nome: hoje aparece o telefone'}
            autoComplete="off"
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contact-email">E-mail</Label>
          <Input
            id="contact-email"
            type="email"
            value={email}
            maxLength={CHAT_REQUESTOR_EMAIL_MAX}
            autoComplete="off"
            onChange={(e) => {
              setEmail(e.target.value);
              setSaved(false);
            }}
          />
        </div>
        {chat.waContact ? (
          <p className="text-xs text-muted-foreground">
            WhatsApp: <span className="text-foreground">{formatWaPhone(chat.waContact)}</span>
          </p>
        ) : chat.requestorPhone ? (
          <p className="text-xs text-muted-foreground">
            Telefone: <span className="text-foreground">{formatWaPhone(chat.requestorPhone)}</span>
          </p>
        ) : null}
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
          {saved && !dirty ? (
            <span className="text-xs text-muted-foreground" role="status">
              Contato salvo.
            </span>
          ) : null}
        </div>
        {error ? (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </form>

      <Separator />

      <section className="space-y-2" aria-label="Cliente">
        <h3 className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">Cliente</h3>
        {chat.client ? (
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/clients/$clientId"
              params={{ clientId: String(chat.client.id) }}
              className="truncate text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
            >
              {chat.client.name}
            </Link>
            {!archived ? (
              <Button variant="ghost" size="xs" onClick={() => setDialog('client')}>
                Alterar
              </Button>
            ) : null}
          </div>
        ) : archived ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <Button variant="outline" size="xs" onClick={() => setDialog('client')}>
            Vincular cliente
          </Button>
        )}
      </section>

      <Separator />

      <section className="space-y-2" aria-label="Ticket">
        <h3 className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">Ticket</h3>
        {chat.ticketNumber !== null ? (
          <div className="min-w-0">
            <Link
              to="/tickets/$ticketNumber"
              params={{ ticketNumber: String(chat.ticketNumber) }}
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary-pressed"
            >
              <Ticket className="size-3.5" />#{chat.ticketNumber}
            </Link>
            {chat.ticketTitle ? <p className="truncate text-xs text-muted-foreground">{chat.ticketTitle}</p> : null}
          </div>
        ) : archived ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <Button variant="outline" size="xs" onClick={() => setDialog('ticket')}>
            Vincular ticket
          </Button>
        )}
      </section>

      {dialog === 'client' ? <LinkClientDialog chat={chat} onClose={() => setDialog(null)} /> : null}
      {dialog === 'ticket' ? <LinkTicketDialog chat={chat} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
